import React from 'react';
import { useTranslations } from 'next-intl';
import { ArrowRightIcon } from '@phosphor-icons/react';
import { Box, Button, Card, CardContent, Grid2 as Grid, Stack, Typography } from '@unity/cloud-ui';
import { SxProps } from '@unity/cloud-ui/styles';
import { NavigationLink } from '@/components/navigation-link';

export interface ProductCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  iconColor?: string;
  displayLinksInColumns?: boolean;
  linkSections?: {
    title: string;
    links?: {
      title: string;
      href: string;
    }[];
  }[];
  sx?: SxProps;
}

export const ProductCard = (props) => {
  const { title, description, href, icon, iconColor, displayLinksInColumns = false, linkSections, sx } = props;
  const t = useTranslations();

  return (
    <Card sx={sx}>
      <CardContent>
        <Stack flexDirection="row" gap={0.5} marginBottom={1.5}>
          <Button
            component={NavigationLink}
            href={href}
            aria-label={t('link:generic:aria-label', { title })}
            color="default"
            endIcon={href ? <ArrowRightIcon /> : undefined}
            sx={{ '&:hover': { color: 'inherit' }, display: 'flex', marginLeft: '-.5rem' }}
          >
            <Box
              display="flex"
              alignItems="center"
              color={iconColor}
              paddingRight={2.5}
              sx={{ '& img': { objectFit: 'contain' } }}
            >
              {icon}
            </Box>
            <Typography variant="h2">{title}</Typography>
          </Button>
        </Stack>
        <Typography color="textSecondary" className="cardDescription">
          {description}
        </Typography>
        {linkSections?.map((linkSection) => (
          <Box key={linkSection.titleKey} marginTop={3}>
            <Typography variant="caption" color="textSecondary" component="div" sx={{ marginBottom: 1.5 }}>
              {t(linkSection.titleKey)}
            </Typography>
            {linkSection.links?.length > 0 &&
              (displayLinksInColumns ? (
                <Grid container rowSpacing={1} columnSpacing={2}>
                  {linkSection.links.map((link) => (
                    <Grid size={6} key={link.href}>
                      <NavigationLink
                        href={link.href}
                        underline="always"
                        aria-label={t('link:generic:aria-label', { title: link.href })}
                      >
                        {t(link.titleKey)}
                      </NavigationLink>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Stack direction="row" gap={1} columnGap={1.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                  {linkSection.links.map((link) => (
                    <NavigationLink
                      key={link.href}
                      href={link.href}
                      underline="always"
                      aria-label={t('link:generic:aria-label', { title: link.href })}
                    >
                      {link.title ?? t(link.titleKey)}
                    </NavigationLink>
                  ))}
                </Stack>
              ))}
          </Box>
        ))}
      </CardContent>
    </Card>
  );
};
